<template>
  <div class="time-area-chart">
    <div class="chart-header">
      <h6 class="chart-title">{{ title }}</h6>
      <div class="chart-filters">
        <select v-model="selectedPeriod" class="form-select form-select-sm" @change="onPeriodChange">
          <option value="7">Últimos 7 días</option>
          <option value="15">Últimos 15 días</option>
          <option value="30">Último mes</option>
          <option value="90">Últimos 3 meses</option>
        </select>
        <select v-model="selectedStatus" class="form-select form-select-sm" @change="onStatusChange">
          <option value="">Todos los estados</option>
          <option v-for="status in availableStatuses" :key="status" :value="status">
            {{ formatStatus(status) }}
          </option>
        </select>
      </div>
    </div>

    <div class="chart-container">
      <div v-if="loadingInternal" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
      <AreaChartGradient
        v-else-if="chartLabels.length > 0"
        :key="'time-' + chartLabels.join('-')"
        :title="''"
        :labels="chartLabels"
        :data="chartData"
        :legend-label="'Tiempo promedio (horas)'"
        :summary="chartSummary"
        :format-tooltip="formatTooltipValue"
        gradient-from="rgba(204, 11, 122, 0.4)"
        gradient-to="rgba(204, 11, 122, 0.02)"
        line-color="#CC0B7A"
        :show-legend="false"
      />
      <div v-else class="no-data">
        <p>No hay datos disponibles para el período seleccionado</p>
      </div>
    </div>

    <!-- Resumen de tiempos por estado -->
    <div class="time-breakdown" v-if="timeBreakdown.length > 0">
      <div class="breakdown-item" v-for="item in timeBreakdown.slice(0, 5)" :key="item.status">
        <div class="breakdown-status">
          <span class="status-dot" :style="{ backgroundColor: getStatusColor(item.status) }"></span>
          <span class="status-name">{{ formatStatus(item.status) }}</span>
        </div>
        <div class="breakdown-time">
          <span class="time-value">{{ formatDuration(item.avgHours) }}</span>
          <span class="time-label">promedio</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import axios from "axios";
import AreaChartGradient from "./AreaChartGradient.vue";

export default {
  name: "StateTimeAreaChart",
  components: {
    AreaChartGradient,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    rawData: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: "Tiempo Medio en Estado",
    },
    statusTranslations: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedPeriod: "7",
      selectedStatus: "",
      chartLabels: [],
      chartData: [],
      chartSummary: null,
      timeBreakdown: [],
      availableStatuses: [],
      internalData: [],
      loadingInternal: false,
    };
  },
  watch: {
    rawData: {
      handler(newData) {
        // Cuando recibimos datos del dashboard, procesarlos
        if (newData && newData.length > 0) {
          this.internalData = newData;
          this.processData();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    // Cargar datos iniciales con el período por defecto
    this.loadDataForPeriod();
  },
  methods: {
    async loadDataForPeriod() {
      this.loadingInternal = true;

      const days = parseInt(this.selectedPeriod);
      const dateFrom = moment().subtract(days - 1, "days").format("YYYY-MM-DD");
      const dateTo = moment().format("YYYY-MM-DD");

      try {
        const isBusinessUser = this.$store?.state?.roles?.Backoffice_member_empresas || false;
        const url = isBusinessUser ? "/business/contract/search" : "/contract/search";

        const params = {
          "pagination.pageSize": 500,
          "pagination.page": 1,
          dateFrom,
          dateTo,
        };

        if (this.selectedStatus) {
          params.statuses = this.selectedStatus;
        }

        const response = await axios.get(url, { params });
        this.internalData = response.data.contracts || [];
        this.processData();
      } catch (error) {
        console.error("Error loading time data:", error);
        // Si falla, usar los datos del rawData
        this.internalData = this.rawData || [];
        this.processData();
      } finally {
        this.loadingInternal = false;
      }
    },

    processData() {
      const dataToProcess = this.internalData.length > 0 ? this.internalData : this.rawData;

      if (!dataToProcess || dataToProcess.length === 0) {
        this.chartLabels = [];
        this.chartData = [];
        this.chartSummary = null;
        this.timeBreakdown = [];
        return;
      }

      // Extraer estados únicos
      this.availableStatuses = [...new Set(dataToProcess.map(item => this.getItemStatus(item)))];

      // Generar días del período
      const days = parseInt(this.selectedPeriod);
      const startDate = moment().subtract(days - 1, "days").startOf("day");
      const endDate = moment().endOf("day");

      const dailyData = {};
      const currentDate = startDate.clone();

      while (currentDate.isSameOrBefore(endDate, "day")) {
        dailyData[currentDate.format("YYYY-MM-DD")] = { totalHours: 0, count: 0 };
        currentDate.add(1, "day");
      }

      // Filtrar datos por estado si hay uno seleccionado
      let filteredData = dataToProcess;
      if (this.selectedStatus) {
        filteredData = dataToProcess.filter(item =>
          this.getItemStatus(item).toUpperCase() === this.selectedStatus.toUpperCase()
        );
      }

      // Calcular breakdown de tiempos por estado (del total sin filtrar)
      const stateTimeData = {};
      dataToProcess.forEach(item => {
        const status = this.getItemStatus(item);
        const createdAt = moment(item.createdAt || item.date || item.dateOfRequest);
        const updatedAt = moment(item.updatedAt || new Date());

        if (createdAt.isValid() && updatedAt.isValid()) {
          const hoursInState = updatedAt.diff(createdAt, "hours");

          if (!stateTimeData[status]) {
            stateTimeData[status] = { total: 0, count: 0 };
          }
          stateTimeData[status].total += hoursInState;
          stateTimeData[status].count += 1;
        }
      });

      this.timeBreakdown = Object.entries(stateTimeData)
        .map(([status, data]) => ({
          status,
          avgHours: data.count > 0 ? data.total / data.count : 0,
        }))
        .sort((a, b) => b.avgHours - a.avgHours);

      // Calcular tiempo promedio por día usando createdAt
      filteredData.forEach(item => {
        const date = moment(item.createdAt || item.date || item.dateOfRequest);
        const createdAt = moment(item.createdAt || item.date || item.dateOfRequest);
        const updatedAt = moment(item.updatedAt || new Date());

        if (date.isValid() && createdAt.isValid() && updatedAt.isValid()) {
          const dayKey = date.format("YYYY-MM-DD");
          if (dailyData[dayKey] !== undefined) {
            const hoursInState = updatedAt.diff(createdAt, "hours");
            dailyData[dayKey].totalHours += hoursInState;
            dailyData[dayKey].count++;
          }
        }
      });

      // Preparar datos para el chart
      const labels = [];
      const data = [];

      Object.keys(dailyData).sort().forEach(dayKey => {
        const dayMoment = moment(dayKey);
        const dayData = dailyData[dayKey];
        labels.push(dayMoment.format("ddd DD"));
        // Promedio de horas para ese día
        const avgHours = dayData.count > 0 ? dayData.totalHours / dayData.count : 0;
        data.push(Math.round(avgHours));
      });

      this.chartLabels = labels;
      this.chartData = data;

      // Calcular resumen
      const validData = data.filter(d => d > 0);
      const total = validData.reduce((a, b) => a + b, 0);
      const avg = validData.length > 0 ? total / validData.length : 0;
      const min = validData.length > 0 ? Math.min(...validData) : 0;
      const max = validData.length > 0 ? Math.max(...validData) : 0;

      this.chartSummary = {
        avg: this.formatDuration(avg),
        min: this.formatDuration(min),
        max: this.formatDuration(max),
      };
    },

    getItemStatus(item) {
      if (item.status) {
        if (typeof item.status === "object") {
          return item.status.status || "UNKNOWN";
        }
        return item.status;
      }
      return item.state || "UNKNOWN";
    },

    formatStatus(status) {
      if (!status) return "Desconocido";
      const upperStatus = status.toUpperCase();
      if (this.statusTranslations[upperStatus]) {
        return this.statusTranslations[upperStatus];
      }
      if (this.statusTranslations[status]) {
        return this.statusTranslations[status];
      }
      return status.replace(/_/g, " ");
    },

    formatDuration(hours) {
      if (!hours || hours === 0) return "-";
      if (hours < 1) return "< 1h";
      if (hours < 24) return `${Math.round(hours)}h`;
      const days = hours / 24;
      if (days < 1) return `${Math.round(hours)}h`;
      return `${days.toFixed(1)} días`;
    },

    formatTooltipValue(value) {
      return this.formatDuration(value);
    },

    getStatusColor(status) {
      const colors = {
        "CREATED": "#6366f1",
        "PENDING_VALIDATION": "#f59e0b",
        "PENDING_SCORING": "#f97316",
        "PENDING_PAYMENT": "#ef4444",
        "ACCEPTED": "#22c55e",
        "ACTIVE": "#10b981",
        "PENDING_DELIVERY": "#8b5cf6",
        "DELIVERED": "#06b6d4",
        "REJECTED": "#dc2626",
        "CANCELLED": "#6b7280",
        "FINISHED": "#14b8a6",
      };
      return colors[status.toUpperCase()] || "#94a3b8";
    },

    onPeriodChange() {
      this.$emit("period-change", this.selectedPeriod);
      this.loadDataForPeriod();
    },

    onStatusChange() {
      this.$emit("status-change", this.selectedStatus);
      this.loadDataForPeriod();
    },
  },
};
</script>

<style scoped lang="scss">
.time-area-chart {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.chart-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.chart-filters {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.form-select-sm {
  padding: 0.375rem 2rem 0.375rem 0.75rem;
  font-size: 0.8125rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  min-width: 140px;

  &:focus {
    border-color: #cc0b7a;
    box-shadow: 0 0 0 2px rgba(204, 11, 122, 0.1);
  }
}

.chart-container {
  min-height: 300px;
}

.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #6b7280;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #6b7280;
}

.time-breakdown {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #f9fafb;
  border-radius: 6px;
}

.breakdown-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.status-name {
  font-size: 0.8125rem;
  color: #374151;
}

.breakdown-time {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.time-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
}

.time-label {
  font-size: 0.6875rem;
  color: #9ca3af;
}

@media (max-width: 768px) {
  .chart-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .chart-filters {
    width: 100%;
  }

  .form-select-sm {
    flex: 1;
    min-width: 0;
  }

  .time-breakdown {
    grid-template-columns: 1fr;
  }
}
</style>
